import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi } from "../../lib/api";
import { inputCls, labelCls } from "./shared";
import { RecruitPanel } from "./RecruitPanel";

export function AffiliatesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("partners");

  const load = useCallback(() => {
    adminApi().get("/admin/affiliates").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await adminApi().put(`/admin/affiliates/${editing.code}`, editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete affiliate ${code}?`)) return;
    await adminApi().delete(`/admin/affiliates/${code}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "number" ? +e.target.value : e.target.value });

  const subTabCls = (v) =>
    `px-4 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${view === v ? "bg-[#970C10] text-white" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <div>
      <div className="flex gap-1 mb-5 bg-white border border-slate-200 rounded-lg p-1 w-fit">
        <button data-testid="affiliates-subtab-partners" className={subTabCls("partners")} onClick={() => setView("partners")}>Partners</button>
        <button data-testid="affiliates-subtab-recruit" className={subTabCls("recruit")} onClick={() => setView("recruit")}>Recruit</button>
      </div>
      {view === "recruit" && <RecruitPanel />}
      <div className={view === "recruit" ? "hidden" : ""}>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name / Email</th>
              <th className="px-4 py-3">Audience</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Commission</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.code} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors duration-150" data-testid={`admin-affiliate-row-${a.code}`}>
                <td className="px-4 py-3 font-mono text-xs">
                  <a href={`/a/${a.code}`} target="_blank" rel="noopener noreferrer" className="text-[#970C10] hover:underline">{a.code}</a>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{a.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{a.email}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{a.audience || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.status === "active" ? "bg-green-100 text-green-700" : a.status === "paused" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {a.per_segment_pkr > 0 && <span className="block">{a.per_segment_pkr}/seg</span>}
                  {a.per_booking_pkr > 0 && <span className="block">{a.per_booking_pkr}/bkg</span>}
                  {a.percent_of_ticket > 0 && <span className="block">{a.percent_of_ticket}% tkt</span>}
                  {a.ad_revenue_share_pct > 0 && <span className="block">{a.ad_revenue_share_pct}% ads</span>}
                  {a.per_segment_pkr === 0 && a.per_booking_pkr === 0 && a.percent_of_ticket === 0 && a.ad_revenue_share_pct === 0 && "—"}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button data-testid={`admin-affiliate-edit-${a.code}`} onClick={() => setEditing({ ...a })} className="text-slate-500 hover:text-[#970C10] transition-colors duration-200">
                    <Pencil className="w-4 h-4 inline" />
                  </button>
                  <button data-testid={`admin-affiliate-delete-${a.code}`} onClick={() => remove(a.code)} className="text-slate-500 hover:text-red-600 transition-colors duration-200">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No affiliates yet — signups will appear here</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-affiliate-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit affiliate</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs text-slate-500 font-mono">Code: <span className="text-slate-900 font-bold">{editing.code}</span></div>
              <div><label className={labelCls}>Name</label><input className={inputCls} value={editing.name} onChange={set("name")} data-testid="affiliate-name-input" /></div>
              <div><label className={labelCls}>Email</label><input className={inputCls} value={editing.email} onChange={set("email")} data-testid="affiliate-email-input" /></div>
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={editing.phone || ""} onChange={set("phone")} /></div>
              <div><label className={labelCls}>Status</label>
                <select className={inputCls} value={editing.status} onChange={set("status")} data-testid="affiliate-status-select">
                  <option value="active">active</option>
                  <option value="paused">paused</option>
                  <option value="banned">banned</option>
                </select>
              </div>
              <div className="col-span-2"><label className={labelCls}>Meta profile URL</label><input className={inputCls} value={editing.meta_profile_url || ""} onChange={set("meta_profile_url")} /></div>
              <div className="col-span-2"><label className={labelCls}>Audience</label><input className={inputCls} value={editing.audience || ""} onChange={set("audience")} /></div>
              <div className="col-span-2 text-xs uppercase tracking-wide text-slate-500 font-bold pt-2">Commission (mixed — set any/all)</div>
              <div><label className={labelCls}>Per segment (PKR)</label><input type="number" className={inputCls} value={editing.per_segment_pkr} onChange={set("per_segment_pkr")} data-testid="affiliate-per-segment-input" /></div>
              <div><label className={labelCls}>Per booking (PKR)</label><input type="number" className={inputCls} value={editing.per_booking_pkr} onChange={set("per_booking_pkr")} data-testid="affiliate-per-booking-input" /></div>
              <div><label className={labelCls}>% of ticket</label><input type="number" step="0.1" className={inputCls} value={editing.percent_of_ticket} onChange={set("percent_of_ticket")} data-testid="affiliate-percent-input" /></div>
              <div><label className={labelCls}>% ad revenue share</label><input type="number" step="0.1" className={inputCls} value={editing.ad_revenue_share_pct} onChange={set("ad_revenue_share_pct")} data-testid="affiliate-adshare-input" /></div>
              <div className="col-span-2"><label className={labelCls}>Notes</label><textarea className={`${inputCls} h-16`} value={editing.notes || ""} onChange={set("notes")} /></div>
            </div>
          )}
          <button onClick={save} data-testid="affiliate-save-btn" className="w-full bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold py-2.5 rounded-lg transition-colors duration-200">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
